import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isMobile } from 'react-device-detect';
import { useIntl } from 'react-intl';
import { Popover, PopoverBody } from 'reactstrap';

import { getLRSActionMapRule } from '../../utils';
import CompletionTracker from '../CompletionTracker';

import './CompletionRequirements.scss';

const propTypes = {

	/**
	 * The resource.
	 */
	resource: PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The rule.
	 */
	rule: PropTypes.shape({

		/**
		 * The rule ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The rule condition.
		 */
		condition: PropTypes.shape({

			/**
			 * The list of subconditions required to complete the rule.
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The subcondition ID.
				 */
				id: PropTypes.string.isRequired,

				/**
				 * True, if the subcondition is active.
				 */
				active: PropTypes.bool,

				/**
				 * The subcondition action.
				 */
				action: PropTypes.string.isRequired,

				/**
				 * The object type related to this subcondition.
				 */
				object_type: PropTypes.string.isRequired,

				/**
				 * The object title related to this subcondition.
				 */
				object_title: PropTypes.string,

				/**
				 * The parent object title related to this subcondition.
				 */
				parent_object_title: PropTypes.string,

				/**
				 * The completion tracker (if any) for the current user for the subcondition.
				 */
				tracker: PropTypes.shape({

					/**
					 * The completion tracker status.
					 */
					status: PropTypes.string.isRequired,

				}),

			})),

		}).isRequired,

	}).isRequired,

	/**
	 * True, if the completion requirements list is visible.
	 */
	isOpen: PropTypes.bool,

	/**
	 * A map of object ID -> rule data for all rules in the resource scope.
	 */
	rulesByObjectID: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The rule ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * True, if the rule is active.
		 */
		active: PropTypes.bool.isRequired,

	})).isRequired,

	/**
	 * A mapping of rule ID -> completion progress percentage for all rules in the resource scope.
	 */
	progressByRuleID: PropTypes.objectOf(PropTypes.number).isRequired,

	/**
	 * The base URL for the resource scope (must end with a forward slash-delimiter at the end).
	 */
	baseURL: PropTypes.string.isRequired,

	/**
	 * The ID of the DOM node to use as the popover target.
	 */
	popoverTargetId: PropTypes.string.isRequired,

	/**
	 * Callback function to handle toggling the completion requirements list.
	 */
	onToggle: PropTypes.func.isRequired,

	/**
	 * Function to handle updating the toolbar visibility state.
	 */
	setShowRequirements: PropTypes.func.isRequired,

};

const CompletionRequirements = (props) => {

	const {
		isOpen,
		setShowRequirements,
		resource,
		rule,
		rulesByObjectID,
		progressByRuleID,
		baseURL,
		popoverTargetId,
		onToggle,
	} = props;

	const intl = useIntl();

	/**
	 * Triggers/maintains tooltip state on keyboard focus of the popover target.
	 */
	useEffect(() => {

		const popoverTarget = document.getElementById(popoverTargetId);

		// Show popover if target has focus
		const onTargetFocusIn = () => {
			setShowRequirements(true);
		};

		// Hide popover if focus is moved from target
		const onTargetFocusOut = () => {
			setShowRequirements(false);
		};

		if (popoverTarget) {
			popoverTarget.addEventListener('focusin', onTargetFocusIn);
			popoverTarget.addEventListener('focusout', onTargetFocusOut);
		}

		return () => {

			if (popoverTarget) {
				popoverTarget.removeEventListener('focusin', onTargetFocusIn);
				popoverTarget.removeEventListener('focusout', onTargetFocusOut);
			}

		};

	}, [popoverTargetId, setShowRequirements]);

	const requirements = rule.condition.subconditions.filter((sub) => sub.active);

	const incompleteReqs =
		requirements.filter((req) => !req.tracker || req.tracker.status !== 'complete');

	const completeReqs =
		requirements.filter((req) => req.tracker && req.tracker.status === 'complete');

	const sortedReqs = incompleteReqs.concat(completeReqs);

	// Get the user's progress for this rule
	const progress = progressByRuleID[rule.id] || 0;

	const content = (
		<div className="requirements-list">
			{ sortedReqs.map((subcondition) => {

				let actionDisplay, objectDisplay, parentObjectDisplay;

				const actionMap = getLRSActionMapRule();

				const objectInfo = actionMap[subcondition.object_type];

				if (!objectInfo || !objectInfo.actions) {
					// eslint-disable-next-line max-len
					console.error(`Missing actions for object_type=${subcondition.object_type}, action=${subcondition.action}`);
					return null;
				}

				const actionInfo = objectInfo.actions[subcondition.action];

				if (!actionInfo) {
					// eslint-disable-next-line max-len
					console.error(`Missing actions for object_type=${subcondition.object_type}, action=${subcondition.action}`);
					return null;
				}

				// set up action display message
				actionDisplay = actionInfo ? intl.formatMessage(actionInfo.display) : subcondition.action;

				actionDisplay = `${actionDisplay.charAt(0).toUpperCase()}${actionDisplay.slice(1)}`;

				// set up object display message
				if (objectInfo && objectInfo.display) {

					objectDisplay = intl.formatMessage(objectInfo.display);

				} else {

					objectDisplay = subcondition.object_title || subcondition.object_type;

				}

				// embed link if specified in action map
				if (objectInfo && objectInfo.getObjectLink && subcondition[objectInfo.referenceKey]) {

					const objectRef = subcondition[objectInfo.referenceKey];

					objectDisplay = (
						<a
							href={`${baseURL}${objectInfo.getObjectLink(objectRef)}`}
							target="_blank"
							rel="noopener noreferrer">
							{objectDisplay}
						</a>
					);
				}

				// set up parent object display message
				if (actionInfo && actionInfo.parentDisplay && subcondition.parent_object_title) {

					parentObjectDisplay = intl.formatMessage(
						actionInfo.parentDisplay,
						{ object_title: subcondition.parent_object_title }
					);

				} else if (subcondition.parent_object_title) {

					parentObjectDisplay = subcondition.parent_object_title;

				}

				// Find corresponding rule for subcondition and check for setup errors
				const subRule = rulesByObjectID[subcondition.object_id];

				const subProgress = subRule && progressByRuleID[subRule.id];

				const status = subcondition.tracker && subcondition.tracker.status;

				return (
					<div key={subcondition.id}>
						<div className="d-flex pt-1 pb-1">
							<div className="d-flex align-items-center me-2">
								<CompletionTracker
									status={status}
									progress={subProgress || 0} />
							</div>
							<div className={`description ${status}`}>
								{actionDisplay}
								{' '}
								{objectDisplay}
								{' '}
								{parentObjectDisplay}
							</div>
						</div>
					</div>
				);

			})}
		</div>
	);

	const progressBar = (
		<div
			className="requirements-progress-bar-container">
			<div
				className="requirements-progress-bar"
				style={{ width: `${progress}%` }} />
		</div>
	);

	return (
		<Popover
			className="tc-requirements-popover"
			innerClassName="tc-requirements-popover-inner"
			target={popoverTargetId}
			trigger={isMobile ? 'click' : 'click hover'}
			aria-live="assertive"
			aria-labelledby={popoverTargetId}
			placement={resource.type === 'slide' ? 'left' : 'top'}
			isOpen={isOpen}
			toggle={onToggle}
			// Add a delay so fade-out doesn't flicker when mousing over popover content
			delay={{ hide: 200 }}>
			<PopoverBody>
				{ content }
			</PopoverBody>
			{progressBar}
		</Popover>
	);

};

CompletionRequirements.propTypes = propTypes;

export default CompletionRequirements;
